import React, { useEffect, useState } from 'react';
import { Chat, Message } from '../types';
import { Users, Search, LogOut, Image, Paperclip, X, Pin } from 'lucide-react';
import './ChatList.css';

interface ChatListProps {
  chats: Chat[];
  messages: Record<string, Message[]>;
  draftsByChat: Record<string, { text: string; html: string }>;
  selectedChatId: string | null;
  onSelectChat: (chatId: string) => void;
  onLogout: () => void;
  isLoading: boolean;
}

const ChatList: React.FC<ChatListProps> = ({ chats, messages, draftsByChat, selectedChatId, onSelectChat, onLogout, isLoading }) => {
  const PINNED_CHATS_STORAGE_KEY = 'innoprogTelegramPinnedChatIds';
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFolder, setActiveFolder] = useState('all');
  const [curatorBadgeByChat, setCuratorBadgeByChat] = useState<Record<string, boolean>>({});
  const [pinnedChatIds, setPinnedChatIds] = useState<string[]>(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(PINNED_CHATS_STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(PINNED_CHATS_STORAGE_KEY, JSON.stringify(pinnedChatIds));
  }, [pinnedChatIds]);

  const togglePinnedChat = (chatId: string) => {
    setPinnedChatIds((current) => current.includes(chatId)
      ? current.filter((id) => id !== chatId)
      : [...current, chatId]);
  };

  const isSameDay = (left: Date, right: Date) => {
    return left.getFullYear() === right.getFullYear()
      && left.getMonth() === right.getMonth()
      && left.getDate() === right.getDate();
  };

  const getWeekStart = (date: Date) => {
    const weekStart = new Date(date);
    const day = weekStart.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    weekStart.setDate(weekStart.getDate() + diffToMonday);
    weekStart.setHours(0, 0, 0, 0);
    return weekStart;
  };

  const formatLastMessageTime = (timestamp: Date) => {
    const messageDate = new Date(timestamp);
    const now = new Date();

    if (isSameDay(messageDate, now)) {
      return messageDate.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (isSameDay(messageDate, yesterday)) {
      return 'вчера';
    }

    const messageWeekStart = getWeekStart(messageDate).getTime();
    const currentWeekStart = getWeekStart(now).getTime();
    if (messageWeekStart === currentWeekStart) {
      return messageDate
        .toLocaleDateString('ru-RU', { weekday: 'short' })
        .replace('.', '');
    }

    return messageDate.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const getAvatarInitials = (name: string) => {
    const source = name.includes('|')
      ? (name.split('|')[1] || '').trim()
      : name;
    const words = source.split(/\s+/).filter(Boolean);
    if (words.length === 0) return '';
    if (words.length === 1) {
      return words[0][0].toUpperCase();
    }
    if (words.length >= 3 && words[1].toLowerCase() === 'и') {
      return (words[0][0] + words[2][0]).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const hasWithTeacherChats = chats.some(chat => chat.with_teacher);
  const NEW_CHATS_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;
  const isNewChat = (chat: Chat) => {
    if (!chat.createdAt) return false;
    const createdAt = chat.createdAt.getTime();
    if (!Number.isFinite(createdAt)) return false;
    const now = Date.now();
    return createdAt <= now && (now - createdAt) <= NEW_CHATS_WINDOW_MS;
  };

  const getMemberDisplayName = (fullName?: string | null, innoprogName?: string | null) => {
    const name = (innoprogName || fullName || '').trim();
    return name.length > 0 ? name : null;
  };

  const getMemberPreviewName = (fullName?: string | null, innoprogName?: string | null) => {
    const name = (innoprogName || fullName || '').trim();
    return name.length > 0 ? name : null;
  };

  const resolveDisplayNameForChatSender = (chat: Chat, senderId?: number, senderRawName?: string, senderFallback?: string) => {
    const members = chat.members || [];
    const normalizedRaw = senderRawName?.replace(/^@/, '').trim().toLowerCase();
    const byId = members.find((member) => {
      const id = member.id ?? member.telegram_id ?? member.user_id ?? member.sender_id;
      return typeof id === 'number' && senderId && id === senderId;
    });
    const byFullName = normalizedRaw
      ? members.find((member) => member.full_name?.trim().toLowerCase() === normalizedRaw)
      : undefined;
    const byInnoprogName = normalizedRaw
      ? members.find((member) => member.innoprog_name?.trim().toLowerCase() === normalizedRaw)
      : undefined;
    const byFullNameCompact = normalizedRaw
      ? members.find((member) => member.full_name?.replace(/\s+/g, '').toLowerCase() === normalizedRaw)
      : undefined;
    const byUsername = normalizedRaw
      ? members.find((member) => {
        const candidate = (member as any).username
          ?? (member as any).user_name
          ?? (member as any).telegram_username
          ?? (member as any).tg_username
          ?? (member as any).telegramUsername
          ?? (member as any).tgUsername
          ?? (member as any).userName;
        return typeof candidate === 'string' && candidate.trim().toLowerCase() === normalizedRaw;
      })
      : undefined;
    const member = byId || byFullName || byInnoprogName || byFullNameCompact || byUsername;
    const display = member ? getMemberDisplayName(member.full_name, member.innoprog_name) : null;
    const fallbackName = senderFallback || senderRawName;
    const normalizedFallback = fallbackName?.replace(/^@/, '').trim().toLowerCase();
    if (normalizedFallback === 'bot') {
      return 'Вы';
    }
    if (normalizedFallback === 'innoprog_tuto_bot' || normalizedFallback === 'innoprog_tutor_bot') {
      return '@Куратор';
    }
    return display || senderFallback || 'Unknown';
  };

  const formatMemberMentions = (chat: Chat, text: string) => {
    if (!text) return text;
    const normalizeSpecialInnoprogMentions = (value: string) => {
      let normalized = value.replace(/(^|[^\w@])@?администратор\s+innoprog(?=$|\s|[.,!?:;)\]])/giu, '$1@innoprog_admin');
      normalized = normalized.replace(/(^|[^\w@])@?координатор\s+innoprog(?=$|\s|[.,!?:;)\]])/giu, '$1@innoprog_coordinator');
      return normalized;
    };
    const members = chat.members || [];
    const byId = new Map<number, string>();
    const aliasMap = new Map<string, string>();
    const addAlias = (rawAlias?: string | null, display?: string | null) => {
      if (!rawAlias || !display) return;
      const trimmed = rawAlias.trim();
      if (!trimmed) return;
      const normalized = trimmed.replace(/^@/, '');
      const collapsed = normalized.replace(/\s+/g, ' ');
      aliasMap.set(collapsed.toLowerCase(), display);
      aliasMap.set(collapsed.replace(/\s+/g, '').toLowerCase(), display);
    };
    members.forEach((member) => {
      const id = member.id ?? member.telegram_id ?? member.user_id ?? member.sender_id;
      const display = getMemberPreviewName(member.full_name, member.innoprog_name);
      if (typeof id === 'number' && display) {
        byId.set(id, display);
      }
      if (display) {
        addAlias(member.full_name, display);
        addAlias(member.innoprog_name, display);
        addAlias((member as any).username, display);
        addAlias((member as any).user_name, display);
        addAlias((member as any).telegram_username, display);
        addAlias((member as any).tg_username, display);
        addAlias((member as any).telegramUsername, display);
        addAlias((member as any).tgUsername, display);
        addAlias((member as any).userName, display);
      }
    });
    let result = text.replace(/<\/?[^>]+>/g, '');
    result = result.replace(/@?innoprog_tuto?r_bot/gi, '@Куратор');
    result = normalizeSpecialInnoprogMentions(result);
    result = result.replace(/\{\{(\d+)\}\}/g, (match, id) => {
      const display = byId.get(Number(id));
      return display ? `@${display}` : match;
    });
    aliasMap.forEach((display, alias) => {
      const escapedAlias = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pattern = new RegExp(`(^|[^\\w@])@${escapedAlias}(?=\\s|$|[.,!?:;])`, 'gi');
      result = result.replace(pattern, `$1@${display}`);
    });
    return normalizeSpecialInnoprogMentions(result);
  };

  const stripHtmlTags = (value: string) => {
    return value.replace(/<\/?[^>]+>/g, '');
  };

  const hasCuratorMention = (chat: Chat, text?: string | null) => {
    if (!text) return false;
    const formatted = stripHtmlTags(formatMemberMentions(chat, text));
    return /@куратор/i.test(formatted);
  };

  const hasUnreadCuratorMention = (chat: Chat) => {
    const chatMessages = messages[chat.id] || [];
    const unreadMessages = chatMessages.filter((message) => !message.isRead && message.sender !== 'me');
    if (unreadMessages.length > 0) {
      return unreadMessages.some((message) => {
        if (!message.text) return false;
        return hasCuratorMention(chat, message.text);
      });
    }
    if (chat.unreadCount > 0 && chat.lastMessage?.text && chat.lastMessage.sender !== 'me') {
      return hasCuratorMention(chat, chat.lastMessage.text);
    }
    return false;
  };

  useEffect(() => {
    setCuratorBadgeByChat((prev) => {
      const next = { ...prev };
      chats.forEach((chat) => {
        if (chat.unreadCount === 0) {
          next[chat.id] = false;
          return;
        }
        if (hasUnreadCuratorMention(chat)) {
          next[chat.id] = true;
        } else if (next[chat.id] === undefined) {
          next[chat.id] = false;
        }
      });
      return next;
    });
  }, [chats, messages]);

  const isGroupMessage = (chat: Chat, message?: Chat['lastMessage']) => {
    if (!message?.text) return false;
    const formatted = formatMemberMentions(chat, message.text);
    return formatted.includes('@Group');
  };

  const isGroupSender = (message?: Chat['lastMessage']) => {
    if (!message) return false;
    return message.senderRawName === '@Group' || message.sender === '@Group';
  };

  const folderFilteredChats = chats.filter((chat) => {
    if (activeFolder === 'all') return true;
    if (activeFolder === 'new') return isNewChat(chat);
    if (activeFolder === 'unread') return chat.unreadCount > 0;
    if (activeFolder === 'with-teacher') return chat.with_teacher;
    return true;
  });

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredChats = folderFilteredChats
    .filter(chat => {
      if (!normalizedSearch) return true;
      if (chat.name.toLowerCase().includes(normalizedSearch)) return true;
      if ((chat.teacherNames || []).some(name => name.toLowerCase().includes(normalizedSearch))) return true;

      const idNeedle = normalizedSearch.replace(/[^\d]/g, '');
      if (!idNeedle) return false;
      const chatIdMatch = String(chat.id).includes(idNeedle);
      const telegramChatIdMatch = chat.telegramChatId !== undefined && String(chat.telegramChatId).includes(idNeedle);
      if (chatIdMatch || telegramChatIdMatch) return true;

      const members = chat.members || [];
      return members.some(member => {
        const ids = [
          member.id,
          member.telegram_id,
          member.user_id,
          member.sender_id
        ].filter((value): value is number => typeof value === 'number');
        return ids.some(value => String(value).includes(idNeedle));
      });
    })
    .sort((a, b) => {
      const aPinned = pinnedChatIds.includes(a.id);
      const bPinned = pinnedChatIds.includes(b.id);
      if (aPinned !== bPinned) return aPinned ? -1 : 1;
      // Если у одного из чатов нет последнего сообщения, он идет вниз
      if (!a.lastMessage && !b.lastMessage) return 0;
      if (!a.lastMessage) return 1;
      if (!b.lastMessage) return -1;

      // Сортировка по времени последнего сообщения (от самого нового к старому)
      return new Date(b.lastMessage.timestamp).getTime() - new Date(a.lastMessage.timestamp).getTime();
    });

  return (
    <div className="chat-list">
      <div className="chat-list-header">
        <div className="header-row">
          <h2>Чаты</h2>
          {isLoading && (
            <div className="chat-list-refresh">
              <span className="chat-list-refresh-spinner" aria-hidden="true"></span>
              <span className="chat-list-refresh-text">Обновление</span>
            </div>
          )}
          <button onClick={onLogout} className="logout-icon-button" title="Выйти">
            <LogOut size={20} />
          </button>
        </div>
        <div className="search-container">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Поиск чатов..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm.length > 0 && (
            <button
              type="button"
              className="chat-search-clear-button"
              onClick={() => setSearchTerm('')}
              aria-label="Очистить поиск"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <div className="chat-folders">
          <button
            className={`chat-folder ${activeFolder === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFolder('all')}
          >
            Все
          </button>
          <button
            className={`chat-folder ${activeFolder === 'new' ? 'active' : ''}`}
            onClick={() => setActiveFolder('new')}
          >
            Новые чаты
          </button>
          <button
            className={`chat-folder ${activeFolder === 'unread' ? 'active' : ''}`}
            onClick={() => setActiveFolder('unread')}
          >
            Непрочитанные
          </button>
          {hasWithTeacherChats && (
            <button
              className={`chat-folder ${activeFolder === 'with-teacher' ? 'active' : ''}`}
              onClick={() => setActiveFolder('with-teacher')}
            >
              С преподавателями
            </button>
          )}
        </div>
      </div>

      <div className="chats">
        {isLoading && chats.length === 0 ? (
          Array.from({ length: 6 }).map((_, index) => (
            <div key={`skeleton-${index}`} className="chat-item chat-item-skeleton">
              <div className="chat-avatar skeleton-block"></div>
              <div className="chat-info">
                <div className="chat-header">
                  <div className="chat-name-container">
                    <span className="skeleton-line skeleton-line-title"></span>
                  </div>
                  <span className="skeleton-line skeleton-line-time"></span>
                </div>
                <div className="chat-preview">
                  <span className="skeleton-line skeleton-line-message"></span>
                </div>
              </div>
            </div>
          ))
        ) : (
          filteredChats.map((chat) => {
            const draft = draftsByChat[chat.id];
            const draftText = draft?.text?.trim() || '';
            const displayLastMessage = !draftText && chat.lastMessage && (!isGroupMessage(chat, chat.lastMessage) || isGroupSender(chat.lastMessage))
              ? chat.lastMessage
              : null;
            return (
            <div
              key={chat.id}
              className={`chat-item ${selectedChatId === chat.id ? 'active' : ''}`}
              onClick={() => onSelectChat(chat.id)}
            >
              <div className="chat-avatar">
                {chat.avatar ? (
                  <img src={chat.avatar} alt={chat.name} />
                ) : (
                  <div className="avatar-placeholder">
                    {getAvatarInitials(chat.name)}
                  </div>
                )}
              </div>

              <div className="chat-info">
                <div className="chat-header">
                  <div className="chat-name-container">
                    {chat.with_teacher ? (
                      <span className="status-icon" aria-hidden="true">🎓</span>
                    ) : (
                      <span className={`status-circle ${chat.statusColor || 'grey'}`}></span>
                    )}
                    <Users size={14} className="group-icon" />
                    <span className="chat-name">{chat.name}</span>
                  </div>
                  {chat.lastMessage && (
                    <span className="chat-time">
                      {formatLastMessageTime(chat.lastMessage.timestamp)}
                    </span>
                  )}
                </div>

                <div className="chat-preview">
                  {draftText ? (
                    <span className="last-message">
                      <span className="draft-label">Черновик: </span>
                      <span className="chat-preview-text">{stripHtmlTags(draftText)}</span>
                    </span>
                  ) : displayLastMessage ? (
                    <>
                      <span className="last-message">
                      {!isGroupSender(displayLastMessage) && (
                        <span className="you-label">
                          {displayLastMessage.sender === 'me'
                            ? 'Вы: '
                            : `${resolveDisplayNameForChatSender(chat, displayLastMessage.senderId, displayLastMessage.senderRawName, displayLastMessage.sender)}: `}
                        </span>
                      )}
                        {(() => {
                          if (isGroupSender(displayLastMessage)) return null;
                          const msg = displayLastMessage;
                          if (!msg.attachments || msg.attachments.length === 0) return null;
                          const firstAtt = msg.attachments[0];
                          return firstAtt.type === 'image' ? (
                            <Image size={14} className="attachment-icon" />
                          ) : (
                            <Paperclip size={14} className="attachment-icon" />
                          );
                        })()}
                        <span className="chat-preview-text">
                          {(() => {
                            const msg = displayLastMessage;
                            if (isGroupSender(displayLastMessage)) {
                              return 'Создан новый чат';
                            }
                            if (msg.isPreviewPending) {
                              return '...';
                            }
                            if (msg.text) {
                              return stripHtmlTags(formatMemberMentions(chat, msg.text));
                            }
                            if (msg.attachments && msg.attachments.length > 0) {
                              const firstAtt = msg.attachments[0];
                              return firstAtt.type === 'image' ? 'Изображение' : 'Документ';
                            }
                            return 'Сообщение';
                          })()}
                        </span>
                      </span>
                      {chat.unreadCount > 0 && displayLastMessage.sender !== 'me' && (
                        <>
                          {(curatorBadgeByChat[chat.id] ?? hasUnreadCuratorMention(chat)) && (
                            <span className="mention-badge">@</span>
                          )}
                          <span className="unread-badge">{chat.unreadCount}</span>
                        </>
                      )}
                    </>
                  ) : (
                    <span className="no-messages">Нет сообщений</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                className={`chat-pin-button ${pinnedChatIds.includes(chat.id) ? 'active' : ''}`}
                onClick={(event) => {
                  event.stopPropagation();
                  togglePinnedChat(chat.id);
                }}
                aria-label={pinnedChatIds.includes(chat.id) ? 'Открепить чат' : 'Закрепить чат'}
                title={pinnedChatIds.includes(chat.id) ? 'Открепить чат' : 'Закрепить чат'}
              >
                <Pin size={15} />
              </button>
            </div>
            );
          })
        )}
      </div>

    </div>
  );
};

export default ChatList;
